import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, {
  completeOnboarding,
  setDigestPreference,
} from '../../services/api';

// ─── Brand tokens (parent interface) ──────────────────────────────────────────

const C = {
  bg: '#F8FAFC',
  surface: '#FFFFFF',
  text: '#1E293B',
  textMuted: '#64748B',
  textSubtle: '#94A3B8',
  accent: '#0066FF',
  accentDark: '#0052CC',
  accentDarker: '#003D99',
  accentSoft: '#EFF4FF',
  accentBorder: '#C7D7FE',
  border: '#E2E8F0',
  borderStrong: '#CBD5E1',
  green: '#10B981',
  greenDark: '#059669',
  greenSoft: '#ECFDF5',
  greenBorder: '#A7F3D0',
  greenText: '#065F46',
  amber: '#F59E0B',
  amberSoft: '#FEF3C7',
  amberBorder: '#FDE68A',
  amberText: '#92400E',
  red: '#EF4444',
  redSoft: '#FEF2F2',
  redBorder: '#FECACA',
  redText: '#B91C1C',
};

const HEADING_FONT = "'Syne', sans-serif";
const BODY_FONT = "'DM Sans', sans-serif";

// ─── Constants ────────────────────────────────────────────────────────────────

const YEAR_GROUPS = [
  { value: 1, label: 'Year 1' }, { value: 2, label: 'Year 2' },
  { value: 3, label: 'Year 3' }, { value: 4, label: 'Year 4' },
  { value: 5, label: 'Year 5' }, { value: 6, label: 'Year 6' },
  { value: 7, label: 'Year 7' }, { value: 8, label: 'Year 8' },
  { value: 9, label: 'Year 9' }, { value: 10, label: 'Year 10' },
  { value: 11, label: 'Year 11' }, { value: 12, label: 'Year 12' },
  { value: 13, label: 'Year 13' },
];

// ─── Shared styles ────────────────────────────────────────────────────────────

const btnPrimary = {
  background: C.accent,
  color: '#fff',
  border: 'none',
  borderRadius: 10,
  padding: '14px 24px',
  fontFamily: BODY_FONT,
  fontSize: 15,
  fontWeight: 600,
  cursor: 'pointer',
  width: '100%',
  minHeight: 48,
  transition: 'background 160ms ease, transform 90ms ease, box-shadow 160ms ease',
  boxShadow: '0 1px 0 rgba(0,61,153,0.4) inset, 0 1px 2px rgba(0,102,255,0.25), 0 4px 12px rgba(0,102,255,0.18)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
};

const btnSecondary = {
  background: '#fff',
  color: C.text,
  border: `1px solid ${C.borderStrong}`,
  borderRadius: 10,
  padding: '10px 18px',
  fontFamily: BODY_FONT,
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
  transition: 'background 160ms ease, border-color 160ms ease',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
};

const labelStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  fontFamily: BODY_FONT,
  fontSize: 13,
  color: C.text,
  fontWeight: 500,
};

const inputStyle = {
  border: `1px solid ${C.border}`,
  borderRadius: 10,
  padding: '12px 14px',
  fontFamily: BODY_FONT,
  fontSize: 15,
  color: C.text,
  outline: 'none',
  background: '#fff',
  transition: 'border-color 160ms ease, box-shadow 160ms ease',
};

const headingStyle = {
  fontFamily: HEADING_FONT,
  fontSize: 26,
  fontWeight: 700,
  color: C.text,
  margin: '0 0 8px',
  letterSpacing: '-0.02em',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function StepIndicator({ current, total }) {
  const labels = ['Welcome', 'Add child', 'Safeguarding', 'Ready'];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 32 }}>
      {Array.from({ length: total }, (_, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <React.Fragment key={i}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              <div style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: done ? C.accent : '#fff',
                border: `2px solid ${done || active ? C.accent : C.border}`,
                color: done ? '#fff' : (active ? C.accent : C.textSubtle),
                fontFamily: BODY_FONT,
                fontSize: 13,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 200ms ease',
                boxShadow: active ? `0 0 0 4px ${C.accent}22` : 'none',
              }}>
                {done ? (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M3 7l3 3 5-6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : i + 1}
              </div>
              <span style={{
                fontSize: 11,
                fontWeight: active ? 600 : 500,
                color: active ? C.text : C.textSubtle,
                fontFamily: BODY_FONT,
                letterSpacing: '0.02em',
              }}>{labels[i]}</span>
            </div>
            {i < total - 1 && (
              <div style={{
                flex: 1,
                height: 2,
                background: i < current ? C.accent : C.border,
                margin: '0 8px',
                marginBottom: 22,
                transition: 'background 240ms ease',
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function PinStrength({ pin }) {
  if (!pin) return null;
  const strength = pin.length >= 6 ? 'Strong' : pin.length >= 5 ? 'Good' : 'Weak';
  const color = pin.length >= 6 ? C.green : pin.length >= 5 ? C.amber : C.red;
  const fill = pin.length >= 6 ? 1 : pin.length >= 5 ? 0.66 : 0.33;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 8 }}>
      <span style={{ display: 'inline-flex', gap: 2 }}>
        {[0, 1, 2].map(i => (
          <span key={i} style={{
            width: 14, height: 4, borderRadius: 2,
            background: (i / 3) < fill ? color : C.border,
            transition: 'background 160ms ease',
          }} />
        ))}
      </span>
      <span style={{ fontSize: 11, color, fontWeight: 600 }}>{strength}</span>
    </span>
  );
}

// ─── Step 1: Welcome ──────────────────────────────────────────────────────────

function StepWelcome({ onNext }) {
  const flowItems = [
    { title: 'Your child asks a question', desc: 'Anything from maths homework to history essays.', icon: '💬' },
    { title: 'Lantern checks it for safety', desc: 'Every message is screened before reaching your child.', icon: '🛡️' },
    { title: 'Lantern helps them think', desc: 'Socratic guidance — never just hands over the answer.', icon: '💡' },
    { title: 'You stay in the loop', desc: 'Full session history visible from your dashboard.', icon: '📊' },
  ];

  return (
    <div>
      <h2 style={headingStyle}>Welcome to Lantern</h2>
      <p style={{ color: C.textMuted, lineHeight: 1.6, fontSize: 15, marginBottom: 28, marginTop: 0 }}>
        A safe, governed AI learning assistant — built for families. Here's how it works:
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 32 }}>
        {flowItems.map(({ title, desc, icon }, i) => (
          <div key={title} style={{
            display: 'flex',
            gap: 14,
            alignItems: 'flex-start',
            padding: '14px 16px',
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 12,
            transition: 'border-color 160ms ease, box-shadow 160ms ease',
          }}>
            <div
              aria-hidden="true"
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: C.accentSoft,
                border: `1px solid ${C.accentBorder}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
                flexShrink: 0,
              }}
            >
              {icon}
            </div>
            <div>
              <p style={{
                margin: 0,
                fontFamily: BODY_FONT,
                fontSize: 14,
                fontWeight: 600,
                color: C.text,
              }}>
                <span style={{
                  display: 'inline-block',
                  width: 18,
                  color: C.textSubtle,
                  fontWeight: 500,
                }}>{i + 1}.</span>
                {title}
              </p>
              <p style={{ margin: '2px 0 0 18px', fontSize: 13, color: C.textMuted, lineHeight: 1.5 }}>{desc}</p>
            </div>
          </div>
        ))}
      </div>
      <p style={{ color: C.textMuted, fontSize: 13, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span aria-hidden="true">⏱</span> Takes about 2 minutes.
      </p>
      <button onClick={onNext} style={btnPrimary}>
        Let's get started
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M3 8h10M9 4l4 4-4 4" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>
  );
}

// ─── Step 2: Add first child ──────────────────────────────────────────────────

export function StepAddChild({ onNext }) {
  const [form, setForm]       = useState({ username: '', yearGroup: '', displayName: '', pin: '' });
  const [error, setError]     = useState(null);
  const [created, setCreated] = useState(null);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    if (!form.username || !form.yearGroup || !form.displayName || !form.pin) {
      return setError('Please fill in all fields.');
    }
    if (!/^\d{4,6}$/.test(form.pin)) {
      return setError('PIN must be 4–6 digits.');
    }

    setLoading(true);
    try {
      const res = await api.post('/parent/children', {
        username:    form.username,
        yearGroup:   parseInt(form.yearGroup, 10),
        displayName: form.displayName,
        pin:         form.pin,
      });
      setCreated(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create child account.');
    } finally {
      setLoading(false);
    }
  }

  const inputWith = (name) => ({
    ...inputStyle,
    borderColor: focused === name ? C.accent : C.border,
    boxShadow: focused === name ? `0 0 0 4px ${C.accent}22` : 'none',
  });

  if (created) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <div style={{
            width: 40,
            height: 40,
            borderRadius: 12,
            background: C.greenSoft,
            border: `1px solid ${C.greenBorder}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M5 10l3 3 7-7" stroke={C.greenDark} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h2 style={{ ...headingStyle, margin: 0 }}>Child account created</h2>
        </div>
        <div style={{
          background: C.greenSoft,
          border: `1px solid ${C.greenBorder}`,
          borderRadius: 12,
          padding: '18px 20px',
          marginBottom: 24,
          marginTop: 16,
        }}>
          <p style={{ margin: 0, color: C.greenText, fontSize: 15 }}>
            <strong>{created.display_name}</strong> ({created.username}) has been added.
          </p>
          <p style={{ margin: '10px 0 0', color: C.greenText, fontSize: 13, lineHeight: 1.5 }}>
            Their username is <strong>@{created.username}</strong>. Write down their PIN somewhere safe — they'll need it to log in.
          </p>
        </div>
        <button onClick={() => onNext(created.username)} style={btnPrimary}>Continue</button>
      </div>
    );
  }

  return (
    <div>
      <h2 style={headingStyle}>Add your first child</h2>
      <p style={{ color: C.textMuted, fontSize: 14, marginBottom: 24, marginTop: 0 }}>
        You can add more children later from your dashboard.
      </p>
      <form onSubmit={handleSubmit} autoComplete="off" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <label style={labelStyle}>
          Child's first name
          <input
            name="child-display-name"
            style={inputWith('displayName')}
            value={form.displayName}
            onFocus={() => setFocused('displayName')}
            onBlur={() => setFocused(null)}
            onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))}
            placeholder="e.g. Jake"
            maxLength={50}
          />
        </label>
        <label style={labelStyle}>
          Username
          <input
            name="child-account-handle"
            style={inputWith('username')}
            value={form.username}
            onFocus={() => setFocused('username')}
            onBlur={() => setFocused(null)}
            onChange={e => setForm(f => ({ ...f, username: e.target.value.replace(/[^a-zA-Z0-9]/g, '') }))}
            placeholder="e.g. jake2025"
            maxLength={30}
          />
          <span style={{ fontSize: 12, color: C.textSubtle, fontWeight: 400 }}>
            Letters and numbers only — they'll use this to log in.
          </span>
        </label>
        <label style={labelStyle}>
          Year group
          <select
            style={inputWith('yearGroup')}
            value={form.yearGroup}
            onFocus={() => setFocused('yearGroup')}
            onBlur={() => setFocused(null)}
            onChange={e => setForm(f => ({ ...f, yearGroup: e.target.value }))}
          >
            <option value="">Select year group</option>
            {YEAR_GROUPS.map(yg => (
              <option key={yg.value} value={yg.value}>{yg.label}</option>
            ))}
          </select>
        </label>
        <label style={labelStyle}>
          <span style={{ display: 'flex', alignItems: 'center' }}>
            PIN <span style={{ color: C.textSubtle, fontWeight: 400, marginLeft: 4 }}>(4–6 digits)</span>
            <PinStrength pin={form.pin} />
          </span>
          <input
            name="child-pin"
            style={inputWith('pin')}
            type="password"
            inputMode="numeric"
            value={form.pin}
            onFocus={() => setFocused('pin')}
            onBlur={() => setFocused(null)}
            onChange={e => setForm(f => ({ ...f, pin: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
            placeholder="••••"
            maxLength={6}
          />
          <span style={{ fontSize: 12, color: C.textSubtle, fontWeight: 400 }}>
            Write this down somewhere safe.
          </span>
        </label>
        {error && (
          <div style={{
            background: C.redSoft,
            border: `1px solid ${C.redBorder}`,
            borderRadius: 10,
            padding: '10px 14px',
            color: C.redText,
            fontSize: 13,
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <circle cx="8" cy="8" r="7" stroke={C.redText} strokeWidth="1.5" />
              <path d="M8 4v5M8 11.5v.5" stroke={C.redText} strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            {error}
          </div>
        )}
        <button type="submit" style={{ ...btnPrimary, opacity: loading ? 0.7 : 1 }} disabled={loading}>
          {loading ? (
            <>
              <span style={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                border: '2px solid rgba(255,255,255,0.4)',
                borderTopColor: '#fff',
                animation: 'ow-spin 700ms linear infinite',
              }} />
              Creating account…
            </>
          ) : 'Create account'}
        </button>
      </form>
    </div>
  );
}

// ─── Step 3: Safeguarding alerts ──────────────────────────────────────────────

function StepAlerts({ onNext, parentEmail }) {
  const [notifStatus, setNotifStatus]     = useState('idle');
  const [testSent, setTestSent]           = useState(false);
  const [digestEnabled, setDigestEnabled] = useState(false);
  const [loading, setLoading]             = useState(false);

  async function requestNotifications() {
    if (!('Notification' in window)) {
      setNotifStatus('unavailable');
      return;
    }
    const permission = await Notification.requestPermission();
    setNotifStatus(permission === 'granted' ? 'granted' : 'denied');
  }

  async function sendTestNotification() {
    setLoading(true);
    try {
      if (notifStatus === 'granted') {
        // eslint-disable-next-line no-new
        new Notification('Lantern test alert', {
          body: 'This is a test safeguarding alert from Lantern.',
          icon: '/favicon.ico',
        });
      }
      setTestSent(true);
    } finally {
      setLoading(false);
    }
  }

  async function handleContinue() {
    if (digestEnabled) {
      try { await setDigestPreference(true); } catch {}
    }
    onNext();
  }

  return (
    <div>
      <h2 style={headingStyle}>Safeguarding alerts</h2>
      <p style={{ color: C.textMuted, lineHeight: 1.6, marginBottom: 24, marginTop: 0, fontSize: 15 }}>
        If Lantern detects something concerning, you'll be alerted immediately. You're always in the loop.
      </p>

      {/* Browser push notifications */}
      <div style={{
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: '18px 20px',
        marginBottom: 14,
        background: C.surface,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background: C.accentSoft,
            border: `1px solid ${C.accentBorder}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <path d="M4 7a5 5 0 0110 0v3l1.5 2h-13L4 10V7z" stroke={C.accent} strokeWidth="1.5" strokeLinejoin="round" />
              <path d="M7.5 14a1.5 1.5 0 003 0" stroke={C.accent} strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: C.text }}>Browser notifications</p>
              <span style={{
                fontSize: 11,
                fontWeight: 600,
                padding: '2px 8px',
                borderRadius: 6,
                background: C.accentSoft,
                color: C.accent,
              }}>Recommended</span>
            </div>
            <p style={{ margin: 0, fontSize: 13, color: C.textMuted, lineHeight: 1.5 }}>
              Get instant alerts on this device whenever something needs your attention.
            </p>
            <div style={{ marginTop: 12 }}>
              {notifStatus === 'idle' && (
                <button onClick={requestNotifications} style={btnSecondary}>
                  Allow notifications
                </button>
              )}
              {notifStatus === 'granted' && (
                <p style={{
                  margin: 0,
                  color: C.greenDark,
                  fontSize: 13,
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}>
                  <svg width="14" height="14" viewBox="0 0 14 14">
                    <path d="M3 7l3 3 5-6" stroke={C.greenDark} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  </svg>
                  Notifications enabled
                </p>
              )}
              {(notifStatus === 'denied' || notifStatus === 'unavailable') && (
                <p style={{ color: C.redText, fontSize: 13, margin: 0 }}>
                  Notifications not available — alerts will be sent to your email instead.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Email always on */}
      <div style={{
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: '18px 20px',
        marginBottom: 14,
        background: C.surface,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background: C.greenSoft,
            border: `1px solid ${C.greenBorder}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <rect x="2" y="4" width="14" height="10" rx="2" stroke={C.greenDark} strokeWidth="1.5" />
              <path d="M2.5 5l6.5 5 6.5-5" stroke={C.greenDark} strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: C.text }}>Email alerts</p>
            <p style={{ margin: '2px 0 0', fontSize: 13, color: C.textMuted }}>
              Always on. Sent to <strong style={{ color: C.text }}>{parentEmail}</strong>.
            </p>
          </div>
        </div>
      </div>

      {/* Test alert */}
      <div style={{ marginTop: 18, marginBottom: 22 }}>
        {!testSent ? (
          <button onClick={sendTestNotification} style={{ ...btnSecondary, opacity: loading ? 0.7 : 1 }} disabled={loading}>
            {loading ? 'Sending…' : 'Send test alert'}
          </button>
        ) : (
          <p style={{
            margin: 0,
            color: C.greenDark,
            fontSize: 13,
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}>
            <svg width="14" height="14" viewBox="0 0 14 14">
              <path d="M3 7l3 3 5-6" stroke={C.greenDark} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </svg>
            Test alert sent — check your phone or email.
          </p>
        )}
      </div>

      {/* Digest toggle */}
      <label style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '14px 16px',
        border: `1px solid ${digestEnabled ? C.accent : C.border}`,
        background: digestEnabled ? C.accentSoft : C.surface,
        borderRadius: 12,
        cursor: 'pointer',
        marginBottom: 24,
        transition: 'all 160ms ease',
      }}>
        <input
          type="checkbox"
          checked={digestEnabled}
          onChange={e => setDigestEnabled(e.target.checked)}
          style={{ width: 18, height: 18, marginTop: 1, accentColor: C.accent, cursor: 'pointer' }}
        />
        <div>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: C.text }}>Weekly learning summary</p>
          <p style={{ margin: '2px 0 0', fontSize: 13, color: C.textMuted }}>
            A digest email every Monday with what your child worked on.
          </p>
        </div>
      </label>

      <button onClick={handleContinue} style={btnPrimary}>Continue</button>
    </div>
  );
}

// ─── Step 4: Ready ────────────────────────────────────────────────────────────

function StepReady({ onFinish, childUsername }) {
  const [loading, setLoading] = useState(false);

  async function handleFinish() {
    setLoading(true);
    try {
      await completeOnboarding();
    } finally {
      setLoading(false);
      onFinish();
    }
  }

  const items = [
    { label: 'Child account created' },
    { label: 'Safeguarding alerts configured' },
    { label: 'Parent dashboard ready' },
  ];

  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{
          width: 64,
          height: 64,
          borderRadius: 20,
          margin: '0 auto 16px',
          background: `linear-gradient(135deg, ${C.accent} 0%, ${C.accentDarker} 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 8px 20px ${C.accent}40`,
        }}>
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <path d="M8 16l5 5 11-12" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <h2 style={{ ...headingStyle, textAlign: 'center', marginBottom: 6 }}>You're all set!</h2>
        <p style={{ margin: 0, color: C.textMuted, fontSize: 15 }}>
          Lantern is ready for your family.
        </p>
      </div>

      <div style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: '8px 0',
        marginBottom: 18,
      }}>
        {items.map((it, i) => (
          <div key={it.label} style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '14px 20px',
            borderBottom: i < items.length - 1 ? `1px solid ${C.border}` : 'none',
          }}>
            <div style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: C.greenSoft,
              border: `1px solid ${C.greenBorder}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <svg width="14" height="14" viewBox="0 0 14 14">
                <path d="M3 7l3 3 5-6" stroke={C.greenDark} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
              </svg>
            </div>
            <span style={{ fontSize: 14, color: C.text, fontWeight: 500 }}>{it.label}</span>
          </div>
        ))}
      </div>

      {childUsername && (
        <div style={{
          background: C.amberSoft,
          border: `1px solid ${C.amberBorder}`,
          borderRadius: 12,
          padding: '14px 18px',
          marginBottom: 24,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
        }}>
          <span aria-hidden="true" style={{ fontSize: 20, lineHeight: 1.2 }}>💡</span>
          <p style={{ margin: 0, fontSize: 13.5, color: C.amberText, lineHeight: 1.55 }}>
            Your child logs in at <strong>{window.location.host}</strong> using username{' '}
            <strong>@{childUsername}</strong> and their PIN.
          </p>
        </div>
      )}

      <button onClick={handleFinish} style={{ ...btnPrimary, opacity: loading ? 0.7 : 1 }} disabled={loading}>
        {loading ? 'Setting up…' : 'Go to my dashboard'}
        {!loading && (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M3 8h10M9 4l4 4-4 4" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>
    </div>
  );
}

// ─── Main wizard ──────────────────────────────────────────────────────────────

const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL;

export default function OnboardingWizard({ parentEmail }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [savedUsername, setSavedUsername] = useState('');
  const [completed, setCompleted] = useState(false);

  function handleFinish() {
    setCompleted(true);
    setTimeout(() => navigate('/parent/dashboard'), 800);
  }

  return (
    <>
      <style>{`
        @keyframes ow-spin { to { transform: rotate(360deg); } }
        @keyframes ow-fade-in { 0% { opacity: 0; transform: translateY(8px); } 100% { opacity: 1; transform: translateY(0); } }
        .ow-step { animation: ow-fade-in 280ms cubic-bezier(0.2,0.8,0.2,1) both; }
        .ow-card { animation: ow-fade-in 320ms cubic-bezier(0.2,0.8,0.2,1) both; }
      `}</style>
      <div style={{
        minHeight: '100vh',
        background: C.bg,
        fontFamily: BODY_FONT,
        color: C.text,
        padding: '40px 20px 60px',
        boxSizing: 'border-box',
      }}>
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          {/* Brand */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 28 }}>
            <div style={{
              width: 32,
              height: 32,
              borderRadius: 9,
              background: `linear-gradient(135deg, ${C.accent} 0%, ${C.accentDarker} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 2px 8px ${C.accent}55`,
            }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M5 2h6v3l-1 1v4l1 1v3H5v-3l1-1V6L5 5V2z" fill="#fff" />
              </svg>
            </div>
            <span style={{
              fontFamily: HEADING_FONT,
              fontSize: 18,
              fontWeight: 700,
              color: C.text,
              letterSpacing: '-0.01em',
            }}>Lantern</span>
          </div>

          <StepIndicator current={step} total={4} />

          <div className="ow-card" style={{
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 16,
            padding: '36px 36px',
            boxShadow: '0 1px 3px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.06)',
          }}>
            <div className="ow-step" key={step}>
              {step === 0 && <StepWelcome onNext={() => setStep(1)} />}
              {step === 1 && (
                <StepAddChild
                  onNext={(username) => {
                    setSavedUsername(username);
                    setStep(2);
                  }}
                />
              )}
              {step === 2 && (
                <StepAlerts
                  onNext={() => setStep(3)}
                  parentEmail={parentEmail || ''}
                />
              )}
              {step === 3 && (
                <StepReady
                  childUsername={savedUsername}
                  onFinish={handleFinish}
                />
              )}
            </div>
          </div>

          {/* Back link */}
          {step > 0 && step < 3 && (
            <div style={{ textAlign: 'center', marginTop: 18 }}>
              <button
                onClick={() => setStep(s => s - 1)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: C.textMuted,
                  fontSize: 13,
                  fontFamily: BODY_FONT,
                  cursor: 'pointer',
                  padding: 6,
                }}
              >
                ← Back
              </button>
            </div>
          )}

          {/* Support footer */}
          {SUPPORT_EMAIL && (
            <p style={{
              textAlign: 'center',
              marginTop: 32,
              fontSize: 12,
              color: C.textSubtle,
              fontFamily: BODY_FONT,
            }}>
              Need help?{' '}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                style={{ color: C.accent, textDecoration: 'none', fontWeight: 500 }}
              >
                {SUPPORT_EMAIL}
              </a>
            </p>
          )}

          {completed && (
            <div role="status" aria-live="polite" style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(248,250,252,0.96)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'ow-fade-in 200ms ease both',
              zIndex: 50,
            }}>
              <div style={{
                fontFamily: HEADING_FONT,
                fontSize: 22,
                fontWeight: 700,
                color: C.text,
                marginBottom: 8,
              }}>
                Loading dashboard…
              </div>
              <div style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                border: `3px solid ${C.border}`,
                borderTopColor: C.accent,
                animation: 'ow-spin 800ms linear infinite',
              }} />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
